import React from 'react';
import { Box, Card, CardContent, LinearProgress, Stack, Typography } from '@mui/material';

const DashboardStatCard = ({
    title,
    value,
    change,
    status,
    icon,
    progress = 0,
    trendData,
    gaugeValue,
    gaugeTotal,
    color,
    onClick,
}) => {
    const numericChange = Number(change) || 0;
    const changeColor = numericChange >= 0 ? '#22c55e' : '#ef4444';
    const safeProgress = Math.max(0, Math.min(progress, 100));
    const hasTrend = Array.isArray(trendData) && trendData.length > 1;
    const hasGauge = typeof gaugeValue === 'number' && typeof gaugeTotal === 'number';

    const renderTrend = () => {
        if (!hasTrend) {
            return null;
        }
        const width = 92;
        const height = 42;
        const min = Math.min(...trendData);
        const max = Math.max(...trendData);
        const range = Math.max(max - min, 1);
        const points = trendData.map((point, index) => {
            const x = (index / (trendData.length - 1)) * width;
            const y = height - ((point - min) / range) * height;
            return `${x},${y}`;
        }).join(' ');

        return (
            <Box sx={{ width }}>
                <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} aria-label="7 day trend">
                    <polyline
                        fill="none"
                        stroke={color}
                        strokeWidth="2.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        points={points}
                    />
                </svg>
            </Box>
        );
    };

    const renderGauge = () => {
        if (!hasGauge) {
            return null;
        }
        const size = 64;
        const stroke = 7;
        const radius = (size - stroke) / 2;
        const circumference = 2 * Math.PI * radius;
        const safeTotal = Math.max(gaugeTotal, 0);
        const safeValue = Math.min(Math.max(gaugeValue, 0), safeTotal);
        const percent = safeTotal > 0 ? Math.round((safeValue / safeTotal) * 100) : 0;
        const dashOffset = circumference - (percent / 100) * circumference;

        return (
            <Box sx={{ width: size, height: size, position: 'relative' }}>
                <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-label="completion gauge">
                    <circle
                        cx={size / 2}
                        cy={size / 2}
                        r={radius}
                        fill="none"
                        stroke="rgba(255,255,255,0.16)"
                        strokeWidth={stroke}
                    />
                    <circle
                        cx={size / 2}
                        cy={size / 2}
                        r={radius}
                        fill="none"
                        stroke={color}
                        strokeWidth={stroke}
                        strokeLinecap="round"
                        strokeDasharray={circumference}
                        strokeDashoffset={dashOffset}
                        transform={`rotate(-90 ${size / 2} ${size / 2})`}
                    />
                </svg>
                <Typography
                    sx={{
                        position: 'absolute',
                        inset: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 12,
                        fontWeight: 700,
                    }}
                >
                    {percent}%
                </Typography>
            </Box>
        );
    };

    return (
        <Card
            onClick={onClick}
            sx={{
                backgroundColor: 'background.paper',
                borderRadius: 3,
                p: 2,
                boxShadow: '0 4px 20px rgba(0,0,0,0.4)',
                transition: '0.2s',
                cursor: onClick ? 'pointer' : 'default',
                '&:hover': {
                    transform: 'translateY(-4px)',
                },
            }}
        >
            <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Stack spacing={1}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                            <Box
                                sx={{
                                    color,
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    '& svg': { fontSize: 32 },
                                }}
                            >
                                {icon}
                            </Box>
                            <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ fontSize: 18, fontWeight: 700 }}
                            >
                                {title}
                            </Typography>
                        </Box>
                        <Typography sx={{ fontSize: 42, fontWeight: 600, lineHeight: 1 }}>{value}</Typography>
                        <Typography sx={{ fontSize: 14, color: changeColor }}>
                            {numericChange >= 0 ? `+${numericChange}` : numericChange} today
                        </Typography>
                    </Stack>
                    {hasTrend ? (
                        renderTrend()
                    ) : hasGauge ? (
                        renderGauge()
                    ) : (
                        <Box sx={{ width: 88 }}>
                            <LinearProgress
                                variant="determinate"
                                value={safeProgress}
                                sx={{
                                    height: 8,
                                    borderRadius: 999,
                                    backgroundColor: 'rgba(255,255,255,0.12)',
                                    '& .MuiLinearProgress-bar': {
                                        backgroundColor: color,
                                    },
                                }}
                            />
                        </Box>
                    )}
                </Box>
                <Box
                    sx={{
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                        mt: 1.5,
                    }}
                />
                <Typography sx={{ fontSize: 13, color: 'text.secondary', mt: 2 }}>
                    {status}
                </Typography>
            </CardContent>
        </Card>
    );
};

export default DashboardStatCard;
